import { Alert, Box, Button, Container, Typography } from "@mui/material";
import arrayMutators from "final-form-arrays";
import { camelCase, isNil } from "lodash";
import { memo, useCallback } from "react";
import { Form } from "react-final-form";

import Flx from "../layout/Flx";
import RffFormSpy from "./shared/RffFormSpy";
import RffGroup from "./shared/RffGroup";
import RffGroupContainer from "./shared/RffGroupContainer";
import { camelCaseToSentence } from "../../utils/strings/camelCaseToSentence";
import { red } from "@mui/material/colors";

/**
 * If your RffTextField or other child components are
 * properly subscribing only to the fields they need,
 * then the main form wrapper doesn't need to subscribe
 * to the entire form state.
 */
function RffForm({
  formSpy,
  id,
  onSubmit,
  children,
  initialValues,
  errorMessage,
  isLoading,
  onCancel,
  loading,
  titleSize = "md",
  maxWidth = false,
  formTitle,
  formDescription,
  className,
  suppressFooter,
  includeBottomSpacing,
  titleContent,
  flexDirection = "column",
  titleEndContent,
  footerStartContent,
  grid,
  startContent,
  gap = "44px",
  submitText = "Submit",
  sx,
  hideBottomValidationErrors = false,
  validate,
}) {
  // Wrap onSubmit in useCallback for stable references:
  const handleOnSubmit = useCallback(
    (formData) => {
      if (onSubmit) {
        onSubmit(formData);
      }
    },
    [onSubmit]
  );

  return (
    <Form
      // Subscribe to validation errors
      subscription={{
        submitting: true,
        hasValidationErrors: true,
        errors: true,
        submitFailed: true,

        //
        // values: true,
        // active: true,
        // modified: true,
      }}
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      mutators={{ ...arrayMutators }}
      validate={validate}
      // Instead of inline render function, pass to a memoized component:
      render={(formRenderProps) => (
        <RffFormInner
          id={id}
          className={className}
          gap={gap}
          formRenderProps={formRenderProps}
          children={children}
          errorMessage={errorMessage}
          isLoading={isLoading}
          onCancel={onCancel}
          loading={loading}
          formTitle={formTitle}
          titleContent={titleContent}
          titleSize={titleSize}
          titleEndContent={titleEndContent}
          maxWidth={maxWidth}
          formDescription={formDescription}
          suppressFooter={suppressFooter}
          grid={grid}
          includeBottomSpacing={includeBottomSpacing}
          flexDirection={flexDirection}
          startContent={startContent}
          footerStartContent={footerStartContent}
          submitText={submitText}
          sx={sx}
          formSpy={formSpy}
          hideBottomValidationErrors={hideBottomValidationErrors}
        />
      )}
    />
  );
}

/**
 * 2) A separate memoized component that receives the formRenderProps.
 * This way, if the parent re-renders but the props and form state haven't changed,
 * this won't re-render unnecessarily.
 */
const RffFormInner = memo(function RffFormInner({
  id,
  formRenderProps,
  children,
  errorMessage,
  onCancel,
  loading,
  suppressFooter,
  startContent,
  maxWidth,
  submitText,
  sx,
  className,
  flexDirection,
  includeBottomSpacing,
  hideBottomValidationErrors,
  grid,
  footerStartContent,
  formSpy,
}) {
  const {
    handleSubmit,
    submitting,
    hasValidationErrors,
    errors,
    submitFailed,
  } = formRenderProps;
  const shouldShowValidationErrors = submitFailed && hasValidationErrors;
  // Create a combined validation error message
  // console.log("errors", errors);
  // const validationErrorMessage =
  //   shouldShowValidationErrors && errors && !hideBottomValidationErrors
  //     ? Object.entries(errors)
  //         // only keep string errors

  //         .filter(([_, error]) => typeof error === "string")
  //         // map each full field path to its nice name
  //         .map(([field]) => {
  //           // grab the last segment of the dot-path
  //           const key = field.split(".").pop();
  //           // replace underscores with spaces
  //           const words = key.replace(/_/g, " ");
  //           // capitalize first letter
  //           return camelCaseToSentence(words);
  //         })
  //     : // .join(", ")
  //       null;
  // const validationErrorMessage =
  //   shouldShowValidationErrors && errors && !hideBottomValidationErrors
  //     ? Object.values(errors)
  //         .filter((error) => typeof error === "string")
  //         .join(", ")
  //     : null;

  const flattenErrors = (errs, prefix = "") =>
    Object.entries(errs).flatMap(([key, val]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (typeof val === "string") {
        return [path];
      } else if (val && typeof val === "object") {
        return flattenErrors(val, path);
      }
      return [];
    });

  // 2) the new combined message
  const validationErrorMessage =
    shouldShowValidationErrors && errors && !hideBottomValidationErrors
      ? flattenErrors(errors).map((fullPath) => {
          // grab only the final segment
          const raw = fullPath.split(".").pop();
          // insert spaces before capitals and underscores, lowercase all
          const words = raw
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .toLowerCase();
          // sentence-case it
          // return words.charAt(0).toUpperCase() + words.slice(1);
          return camelCaseToSentence(words);
        })
      : // .join(", ")
        null;

  return (
    <Box
      component="form"
      id={id}
      className={`rff-form-root ${className}`}
      onSubmit={handleSubmit}
      sx={{
        width: "inherit",
        display: "flex",
        flexDirection: "column",
        // gap: "36px",
        ...sx,
      }}
    >
      {startContent}
      <Container
        sx={{
          p: "0 !important",
          //
        }}
        maxWidth={maxWidth}
      >
        {grid ? (
          <RffGroup rowSpacing={2.5}>{children}</RffGroup>
        ) : (
          <RffGroupContainer
            flexDirection={flexDirection}
            suppressFooter={suppressFooter}
          >
            {children}
          </RffGroupContainer>
        )}

        <RffFormFooter
          suppressFooter={suppressFooter}
          onCancel={onCancel}
          loading={loading}
          submitText={submitText}
          footerStartContent={footerStartContent}
          validationErrorMessage={validationErrorMessage}
        />
        {/* Only render the spy if needed */}
        <RffFormSpy formSpy={formSpy} />
        <ErrorMessage error={errorMessage}>{errorMessage}</ErrorMessage>
      </Container>
    </Box>
  );
});

/**
 * The footer can also be memoized if props don't change often
 * (some will skip this if they frequently show `pristine` or `submitting`).
 */
const RffFormFooter = memo(function RffFormFooter({
  onCancel,
  disabled,
  loading,
  submitText = "Submit",
  suppressFooter,
  footerStartContent,
  validationErrorMessage,
}) {
  if (suppressFooter) return null;

  return (
    <Flx
      column
      sx={{
        mt: 1,
        width: "100%",
      }}
    >
      {/* Show validation error message if exists */}
      {validationErrorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography fontWeight={600} color="error">
            Please review and fill out the required fields:
          </Typography>
          <Flx gap={1} wrap sx={{ mt: 1 }}>
            {validationErrorMessage?.map((e) => {
              return (
                <Box
                  key={e}
                  sx={{
                    background: red[50],
                    border: `1px solid ${red[200]}`,
                    borderRadius: 6,
                    px: 1,
                  }}
                >
                  <Typography
                    color="error"
                    key={camelCase(e)}
                    sx={{
                      textTransform: "capitalize",
                      // textAlign: "left",
                    }}
                  >
                    {e}
                  </Typography>
                </Box>
              );
            })}
          </Flx>

          {/* <Typography
            color="error"
            sx={{
              mb: 2,
              width: "100%",
              textAlign: "left",
            }}
          >
            {validationErrorMessage}
          </Typography> */}
        </Alert>
      )}

      <Flx
        fw
        end
        sx={{
          width: "100%",
        }}
      >
        {footerStartContent}
        <Flx>
          {onCancel ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={onCancel}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            // If you have a custom loading button, pass `loading`
            disabled={disabled}
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            loading={loading}
          >
            {submitText}
          </Button>
        </Flx>
      </Flx>
    </Flx>
  );
});

const ErrorMessage = memo(function ErrorMessage({ error, children }) {
  if (isNil(error)) {
    return null;
  }
  return <Typography color="error">{children}</Typography>;
});

export default memo(RffForm);
